import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { FontManagerComponent } from '@app/shared/components';
import { CreativesetShowcaseService } from '@app/shared/creativeset-showcase/state/creativeset-showcase.service';
import { UIConfirmDialogService, UIDialogService, UIDropdownComponent } from '@bannerflow/ui';
import { Subject, Observable, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { CreativesetDataService } from '../../../shared/creativeset';
import { CampaignService } from '../../../shared/services';
import { UserService } from '../../../shared/user/state/user.service';
import { AnimationControlService } from '@app/shared/animation-control/animation-control.service';
import { DuplicateCreativeService } from '../duplicate-creative-dialog/duplicate-creative.service';
import { EditCreativeService, TileSelectService } from '../services';
import { ShowcaseDialogComponent } from '../showcase/showcase-dialog.component';
import { VersionDialogService } from '../../../shared/components/version-dialog';

@Component({
    selector: 'manage-menu',
    templateUrl: './manage-menu.component.html',
    styleUrls: ['./manage-menu.component.scss']
})
export class ManageMenuComponent implements OnDestroy {
    @Input() hasDesigns: boolean;
    @ViewChild('dropdown', { static: true }) dropdown: UIDropdownComponent;

    selectedCreatives: number;
    isAnySelected = false;
    countText: string;
    selectedText: string;
    hasConnectedCampaigns: boolean;
    addToCampaignDisabled: boolean;
    addToCampaignText: string;
    isEmployee$: Observable<boolean>;

    private unsubscribe$ = new Subject<void>();

    constructor(
        private versionDialogService: VersionDialogService,
        private uiConfirmDialogService: UIConfirmDialogService,
        private creativesetData: CreativesetDataService,
        private tileSelectService: TileSelectService,
        private editCreativeService: EditCreativeService,
        private dialogService: UIDialogService,
        private animationControlService: AnimationControlService,
        private duplicationService: DuplicateCreativeService,
        private creativesetShowcaseService: CreativesetShowcaseService,
        private campaignService: CampaignService,
        public userService: UserService
    ) {
        this.tileSelectService.selection$.pipe(takeUntil(this.unsubscribe$)).subscribe(creatives => {
            this.selectedCreatives = creatives.filter(c => c.design).length;
            const count = creatives.length;
            this.isAnySelected = creatives.length > 0;
            this.countText = count >= 1 ? `(${count})` : '';
            this.selectedText = this.selectedCreatives >= 1 ? `(${this.selectedCreatives})` : '';
        });

        this.hasConnectedCampaigns = this.hasAnyConnectedCampaigns();
        this.isEmployee$ = this.userService.isEmployee$;
    }

    duplicateSize(duplicateToNew: boolean): void {
        this.duplicationService.openDialog(duplicateToNew);
    }

    manageVersions = (): void => this.versionDialogService.openVersionDialog();

    fontManagerClosed(): void {
        this.creativesetData.syncFonts();
    }

    manageFonts(): void {
        const dialog = this.dialogService.openComponent(FontManagerComponent, {
            padding: 0,
            headerText: 'Manage brand fonts',
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            panelClass: ['inlined-iframe', 'fullscreen'],
            data: {
                fontManagerUri: `${environment.origins.fontManager}/in-frame/${this.creativesetData.brand.id}`
            }
        });
        firstValueFrom(dialog.beforeClose()).then(() => this.fontManagerClosed());
    }

    async regenerateSet(): Promise<void> {
        this.dropdown.closePopover();

        const res = await this.uiConfirmDialogService.confirm({
            headerText: 'FORCE UPDATE',
            text: 'This action will force update checksums on all the creatives, making it possible to publish without any changes',
            cancelText: 'CANCEL',
            confirmText: 'FORCE UPDATE'
        });

        if (res === 'confirm') {
            await this.editCreativeService.forceUpdateChecksums();
        }
    }

    navitageToAnalytics(): void {
        const url = `${
            environment.origins.b2
        }/v2/${this.creativesetData.getAccountSlug()}/${this.creativesetData.getBrandSlug()}/analytics/stbannerset/${
            this.creativesetData.creativeset.id
        }/dashboard`;
        window.open(url, '_blank');
    }

    async exit(): Promise<void> {
        const brandUrl = `${this.creativesetData.getAccountSlug()}/${this.creativesetData.getBrandSlug()}`;
        const creativesetId = this.creativesetData.creativeset.id;
        let url: string;

        if (await this.userService.hasPermission('CommonListView')) {
            url = `${environment.origins.bfc}/${brandUrl}/creative-sets?redirect=${creativesetId}`;
        } else {
            const folderId = await firstValueFrom(this.creativesetData.getFolderId(creativesetId));
            url = `${environment.origins.b2}/${brandUrl}/BannerSets`;
            if (folderId) {
                url += `/f/${folderId}`;
            }
        }

        window.location.href = url;
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    sharePublicShowcaseDialog(): void {
        // stop animations when showing shared links
        this.animationControlService.stopAnimations();

        this.dialogService.openComponent(ShowcaseDialogComponent, {
            headerText: 'Share Creative set',
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            padding: 0,
            panelClass: ['no-padding', 'fullscreen']
        });
    }

    showMobilePreviewModal(): void {
        this.creativesetShowcaseService.openQRLinkModal();
    }

    redirectToCreateCampaign(): void {
        this.campaignService.redirectToCreateCampaign();
    }

    hasAnyConnectedCampaigns = (): boolean => {
        return !!this.creativesetData.creativeset.creatives.find(creative =>
            this.campaignService.isCreativeConnectedToCampaign(creative)
        );
    };
}
